import { Injectable, NgZone } from '@angular/core';
import { BaseSocket } from '../base.socket';
import { ConfigurationService } from '../../configuration/configuration.service';
import { AuthService } from '@app/modules/auth';
import { SocketType, SubjectType } from '../websocket.enums';
import { Subject } from 'rxjs';
import { EventsService } from '../../events/events.service';
import { CustomEvents, ENABLE_FO_LINK, GLOBAL_EXIT_SETTINGS, MANAGE_BROKERS_PATH, MAP_ACCOUNT_LINK, StorageHelper, Utils } from '@app/_helpers';
import { STREAM_TYPE, SIDEBAR_BUBBLE_EVENTS } from '@app/_shared/shared.enums';
import { InfraUpdate, NgxToastrService } from '../../../providers';
import { WebAlertStatus } from '../../../../../utProto/webAlertStructureEnums_pb';
import { v4 as uuid } from 'uuid';
import { AuthRole } from '../../../../modules/auth/provider/auth/auth.structs';
import { RouteMap } from '@app/_shared/shared.structs';
import { ENABLE_FO, GLOBAL_EXIT_BUTTON, MAP_ACCOUNT, ORDER_BOOK } from '@app/_shared/strings';
import { BUILD_DEALER } from '@app/_helpers/configuration';
import { TriggerType, WebApiStrategyResponseType, WebApiTerminationReasonType } from '../../../../../utProto/webApiStrategyEnums_pb';
import { MDLoginType, Request } from '../../../../../utProto/marketData_pb';
import { TransactionStatus } from '@app/modules/trading/modules/profile/components/plans/plans.enums';
import { MixpanelService } from '../../mixpanel/mixpanel.service';
import { AuthEventType } from '@app/modules/trading/modules/profile/components/active-session/active-session.model';

@Injectable({
    providedIn: 'root'
})
export class BooksSocketService extends BaseSocket {
    private subjectMap = new Map<SubjectType, Subject<any>>();
    uuid = StorageHelper.Uuid;
    global_seq_number = 0;
    timeout = null;
    isFirstConnection = true;
    // Generate new socket ID on new instance (i.e. Web is reloaded)
    socketID = uuid();
    private _adminDeployedClient = null;
    private _role: AuthRole;


    constructor(
        private authService: AuthService,
        private confService: ConfigurationService,
        private eventService: EventsService,
        private snackBar: NgxToastrService,
        private mixpanelService: MixpanelService,
        private _ngZone: NgZone
    ) {
        super(authService, mixpanelService, confService, SocketType.ORDER_DATA_SOCKET, _ngZone);
        this.socketUrl = Utils.changeUrlToWss(this.confService.applicationUrls.orderSocket);
        this._role = this.authService?.accessTokenObj?.role
    }

    acknowledgeSequenceNumber() {
        // Timeout already queued
        if (this.timeout) { return; }

        this.timeout = setTimeout(() => {
            this.timeout = null;
            const message = {
                command: 12,
                global_seq_number: this.global_seq_number,
            };

            this.send(message);
        }, 3000)
    }

    onAuthenticated(): void {
        super.onAuthenticated();

        if (!this.isFirstConnection) {
            this.eventService.publish(CustomEvents.StreamReconnected);
        } else {
            this.isFirstConnection = false;
        }
    }

    sendClientAuthMessage(token: string){
        const message = {
            command: Request.REQUEST_LOGIN,
            login_type: MDLoginType.MDLOGINTYPE_JWT_TOKEN,
            is_child_client: true,
            token
        };
        this.send(message);
    }

    getMtMUpdate(clientID: string, is_subscribe_request: boolean, uniquePortfolioID?: string[]){
        const message: any = {
            command: 13,
            clientID,
            is_subscribe_request
        }
        if(uniquePortfolioID){
            message.uniquePortfolioID = uniquePortfolioID;
        }
        this.send(message);
    }

    getSubject<T = any>(type: SubjectType) {
        if (!this.subjectMap.has(type)) {
            this.subjectMap.set(type, new Subject<T>());
        }

        return this.subjectMap.get(type) as Subject<T>;
    }

    set adminDeployedClient(client_id: string) {
        this._adminDeployedClient = client_id;
    }

    tapMessage(msg: any) {
        super.tapMessage(msg);

        if (msg.message.global_seq_number) {
            this.global_seq_number = Math.max(this.global_seq_number, msg.message.global_seq_number);
            this.acknowledgeSequenceNumber();
        }

        if(msg?.message?.clientId && msg.message.clientId !== this.authService.accessTokenObj.clientId && StorageHelper.operatorToken){
            if(msg.message.stream === STREAM_TYPE.STRATEGY){
                this.getSubject(SubjectType.OPERATOR_CLIENT_STRATEGY).next(msg.message.payload);
            } else if(msg.message.stream === STREAM_TYPE.CLIENT_MTM_UPDATE){
                this.getSubject(SubjectType.CLIENT_MTM_UPDATE).next(msg.message.payload);
            }
        } else {
            if (msg.message.stream === STREAM_TYPE.ORDERS) {
                this.eventService.publish(CustomEvents.SideBarNotification, SIDEBAR_BUBBLE_EVENTS.BOOKS);
                const data = msg.message.payload;
                if (data.order_book_data && data.order_book_data.length) {
                    this.getSubject(SubjectType.ORDERS).next(data.order_book_data);
                }
    
                if (data.trade_book_data && data.trade_book_data.length) {
                    this.getSubject(SubjectType.TRADES).next(data.trade_book_data);
                }
    
                if (data.positions_data && data.positions_data.length) {
                    this.getSubject(SubjectType.POSITIONS).next(data.positions_data);
                }
            } else if (msg.message.stream === STREAM_TYPE.ALERTS && msg.message.payload.length) {
                const conditionStatus = msg.message.payload[0].condition_status;
                // Alerts badge need not to be shown when the alert is rejected or timed out RA-419
                if (conditionStatus === WebAlertStatus.WEBALERTSTATUS_PENDING) {
                    this.eventService.publish(CustomEvents.SideBarNotification, SIDEBAR_BUBBLE_EVENTS.ALERT);
                }
    
                this.getSubject(SubjectType.ALERTS).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.BACKTEST && msg.message.payload) {
                this.getSubject(SubjectType.BACKTEST).next(msg.message.payload);
    
            } else if (msg.message.stream === STREAM_TYPE.STRATEGY && msg.message.payload && !StorageHelper.operatorAdminToken) {
                // For admin, we have to process updates for clients which is selected in deployed tab and ignore other updates.
                if ((msg.message.payload.message || msg.message.payload.TerminationMessage) && !BUILD_DEALER) {
                    const portfolioId = msg.message.payload.childStrategyId === "" ? msg.message.payload.algoName : msg.message.payload.portfolioId;
                    const uniquePortfolioID = msg.message.payload.childStrategyId === "" ? msg.message.payload.uniqueAlgoName : msg.message.payload.childParentId;
                    if (msg.message.payload.TerminationMessage &&
                        (msg.message.payload.strategyTerminationType === WebApiTerminationReasonType.WEBAPITERMINATIONREASONTYPE_STG_EXPIRED ||
                            msg.message.payload.strategyTerminationType === WebApiTerminationReasonType.WEBAPITERMINATIONREASONTYPE_INVALID_EXECUTION_DAY)) {
                        this.snackBar.clickableCustomToaster(msg.message.payload.TerminationMessage, null, null, msg.message.payload.messageDesc, portfolioId, uniquePortfolioID);
                        if (this._role !== AuthRole.ADMIN || (this._role === AuthRole.ADMIN && this._adminDeployedClient === msg.message.payload.clientId)) {
                            this.getSubject(SubjectType.STRATEGY).next(msg.message.payload);
                        }
                        return;
                    }
                    if (msg.message.payload.messageDesc) {
                        let btnText = ORDER_BOOK;
                        let route: string = RouteMap.review;
                        if (msg.message.payload.strategyTerminationType === WebApiTerminationReasonType.WEBAPITERMINATIONREASON_CLIENT_CODE_NOT_FOUND) {
                            route = ENABLE_FO_LINK;;
                            btnText = ENABLE_FO
                        } else if (msg.message.payload.strategyTerminationType === WebApiTerminationReasonType.WEBAPITERMINATIONREASON_RMS_OUTCOME_FAILURE) {
                            route = MAP_ACCOUNT_LINK;
                            btnText = MAP_ACCOUNT
                        }
                        this.eventService.publish(CustomEvents.strategyTerminated, msg.message.payload);
                        this.snackBar.clickableCustomToaster(msg.message.payload.message, btnText, route, msg.message.payload.messageDesc, portfolioId, uniquePortfolioID);
                    } else {
                        const isModifyUnsuccessful = msg.message.payload.webApiStrategyResponseType === WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_MODIFY_UNSUCCESSFUL;
                        const message = isModifyUnsuccessful 
                            ? msg.message.payload.message
                            : (msg.message.payload.message || msg.message.payload.TerminationMessage);
                        isModifyUnsuccessful 
                            ? this.snackBar.clickableCustomToaster(message)
                            : this.snackBar.simpleCustomToaster(message);
                    }
    
                }
                if (this._role !== AuthRole.ADMIN || (this._role === AuthRole.ADMIN && this._adminDeployedClient === msg.message.payload.clientId)) {
                    this.getSubject(SubjectType.STRATEGY).next(msg.message.payload);
                }
            } else if (msg.message.stream === STREAM_TYPE.SYMBOLADDED && msg.message.payload) {
                if (this.uuid !== msg.message.payload.data.uuid) {
                    this.getSubject(SubjectType.SYMBOLADDED).next(msg.message.payload);
                }
    
            } else if (msg.message.stream === STREAM_TYPE.SYMBOLDELETED && msg.message.payload) {
                if (this.uuid !== msg.message.payload.data.uuid) {
                    this.getSubject(SubjectType.SYMBOLDELETED).next(msg.message.payload);
                }
    
            } else if (msg.message.stream === STREAM_TYPE.SYMBOLPOSITIONUPDATED && msg.message.payload) {
                if (this.uuid !== msg.message.payload.data.uuid) {
                    this.getSubject(SubjectType.SYMBOLPOSITIONUPDATED).next(msg.message.payload);
                }
    
            } else if (msg.message.stream === STREAM_TYPE.LISTALGOS && msg.message.payload) {
                if (!BUILD_DEALER && !msg.message.payload.publicPortfolioUpdated) {
                    this.snackBar.simpleCustomToaster(msg.message.payload.message);
                }
                this.getSubject(SubjectType.LISTALGOS).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.STATUS && msg.message.payload) {
                this.getSubject(SubjectType.STATUS).next(msg.message.payload['dms_connection_status']);
            } else if (msg.message.stream === STREAM_TYPE.BROKER && msg.message.payload) {
                this.getSubject(SubjectType.BROKER).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.SQUAREOFF && msg.message.payload) {
                const portfolioId = msg.message.payload.algoName;
                const uniquePortfolioID = msg.message.payload.portfolioId;
                if (msg.message.payload.message && !BUILD_DEALER) {
                    this.snackBar.clickableCustomToaster(msg.message.payload.message, ORDER_BOOK, RouteMap.review, msg.message.payload.messageDesc, portfolioId, uniquePortfolioID);
                }
                this.getSubject(SubjectType.manualSquareOff).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.SUBSCRIPTION) {
                if (msg.message.payload && (!msg.message.payload.hasOwnProperty('status') ||
                    (msg.message.payload.hasOwnProperty('status') && msg.message.payload['status'] === TransactionStatus.FAILED))
                    && !BUILD_DEALER) {
                    this.snackBar.simpleCustomToaster(msg.message.payload.message);
                }
    
                this.getSubject(SubjectType.SUBSCRIPTION).next(msg.message.payload);
    
            } else if (msg.message.stream === STREAM_TYPE.TRANSACTIONS) {
                if (msg.message?.payload?.message && !BUILD_DEALER) {
                    const payload = msg.message.payload;
    
                    //skipping the toaster message in case of transaction insertion and modification
                    if (!(payload.insert || payload.update)) {
                        this.snackBar.simpleCustomToaster(msg.message.payload.message);
                    }
    
                    this.getSubject(SubjectType.TRANSACTIONS).next(msg.message.payload.transaction_body);
                }
            } else if (msg.message.stream === STREAM_TYPE.AUTH && msg.message.payload) {
                //If current session id matches with logout session Id, the  logout that user
                if (msg.message.payload.event === AuthEventType.BLACKLISTED_SESSION && msg.message.payload.session_id === this.authService.accessTokenObj.sessionID) {
                    this.authService.logoutApi();
                }
                this.getSubject(SubjectType.AUTH).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.MARGINBUCKET && msg.message.payload) {
                this.getSubject(SubjectType.MARGINBUCKET).next();
            } else if (msg.message.stream === STREAM_TYPE.INFRASTATUS && msg.message.payload) {
                const infra = msg.message.payload as InfraUpdate;
                if (this.confService.serverConfig) {
                    this.confService.serverConfig.is_infra_connected = infra;
                }
                this.getSubject(SubjectType.INFRASTATUS).next(infra);
            } else if (msg.message.stream === STREAM_TYPE.GLOBALEXIT) {
                if (msg.message.payload.triggerType === TriggerType.TRIGGERTYPE_STOP_LOSS_EXIT) {
                    const route = GLOBAL_EXIT_SETTINGS;
                    const btnText = GLOBAL_EXIT_BUTTON;
                    this.snackBar.clickableCustomToaster(msg.message.payload.message, btnText, route);
                } else {
                    this.snackBar.simpleCustomToaster(msg.message.payload.message, false);
                }
                this.getSubject(SubjectType.GLOBALEXIT).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.NOTIFICATIONS && msg.message.payload) {
                this.getSubject(SubjectType.NOTIFICATIONS).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.AUTODEPLOYMENT) {
                this.getSubject(SubjectType.AUTODEPLOYMENT).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.BROKER_CONNECTION_UPDATE) {
                this.getSubject(SubjectType.BROKER_CONNECTION_UPDATE).next(msg.message.payload);
            } else if (msg.message.stream === STREAM_TYPE.BROKER_LOGIN_UPDATE) {
                const payload = msg.message.payload;
                if(payload.is_connected){
                    this.snackBar.simpleCustomToaster(payload.message);
                } else {
                    this.snackBar.clickableCustomToaster(payload.message, "Reconnect Broker", MANAGE_BROKERS_PATH);
                }
            } else if (msg.message.stream === STREAM_TYPE.BROKER_CREDENTIALS_UPDATE) {
                this.getSubject(SubjectType.BROKER_CREDENTIALS_UPDATE).next(msg.message.payload);
            } else if(msg.message.stream === STREAM_TYPE.BROKER_CONNECTION_SNAPSHOT && msg.message.payload) {
                const connectedBrokersData = msg.message.payload.data;
                // manage broker service dependency removed, as it was causing circular dependency.
                this.getSubject(SubjectType.BROKER_CONNECTION_SNAPSHOT).next(connectedBrokersData.length ? msg.message.payload : null);
            }
        }        
    }
}
